import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { usePAYGDashboard } from '../applicationLayer/dashBoardHooks/paygDashboardHook';
import { VsCodeAuthContext } from '../contextApis/vsCodeAuthContext';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Switch } from '../components/ui/Switch';
import { Input } from '../components/ui/Input';
import { Skeleton } from '../components/ui/Skeleton';
import { cn } from '../lib/utils';
import sixthLogo from '../assets/sixth_logo_white.png';
import PlanSwitcher from '../components/dashBoardComponents/PlanSwitcher';
import {
  Wallet,
  CreditCard,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronRight,
  LogOut,
  Zap,
  Clock,
  TrendingUp,
  Plus,
  AlertCircle,
  Loader2,
  FileText,
  Download,
  MessageSquare,
  Mail,
  Copy,
  Check,
  ExternalLink,
  Sparkles
} from 'lucide-react';

// Skeleton Components for each section
const BalanceSkeleton = () => (
  <Card className="bg-[#121219]/80 border-white/10">
    <CardHeader>
      <div className="flex items-center gap-2">
        <Skeleton className="w-5 h-5 rounded" />
        <Skeleton className="h-6 w-32" />
      </div>
    </CardHeader>
    <CardContent>
      <div className="flex items-center justify-between mb-6">
        <div>
          <Skeleton className="h-10 w-32 mb-2" />
          <Skeleton className="h-4 w-24" />
        </div>
        <Skeleton className="h-10 w-28" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-2 w-full rounded-full" />
        <div className="flex justify-between">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-32" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const TiersSkeleton = () => (
  <Card className="bg-[#121219]/80 border-white/10">
    <CardHeader>
      <div className="flex items-center gap-2">
        <Skeleton className="w-5 h-5 rounded" />
        <Skeleton className="h-6 w-28" />
      </div>
      <Skeleton className="h-4 w-64 mt-2" />
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-20 rounded-lg" />
        ))}
      </div>
    </CardContent>
  </Card>
);

const CardSkeleton = () => (
  <Card className="bg-[#121219]/80 border-white/10">
    <CardHeader>
      <div className="flex items-center gap-2">
        <Skeleton className="w-5 h-5 rounded" />
        <Skeleton className="h-6 w-36" />
      </div>
    </CardHeader>
    <CardContent>
      <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
        <div className="flex items-center gap-4">
          <Skeleton className="w-12 h-8 rounded" />
          <div>
            <Skeleton className="h-5 w-40 mb-2" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-8 w-16" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const TransactionsSkeleton = () => (
  <Card className="bg-[#121219]/80 border-white/10">
    <CardHeader>
      <div className="flex items-center gap-2">
        <Skeleton className="w-5 h-5 rounded" />
        <Skeleton className="h-6 w-40" />
      </div>
    </CardHeader>
    <CardContent>
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
            <div className="flex items-center gap-3">
              <Skeleton className="w-8 h-8 rounded-full" />
              <div>
                <Skeleton className="h-4 w-32 mb-1" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
            <Skeleton className="h-5 w-16" />
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const PAYGDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { logout } = useContext(VsCodeAuthContext);
  const [activeTab, setActiveTab] = useState('credits');

  const {
    userInfo,
    balance,
    tiers,
    savedCard,
    transactions,
    transactionsHasMore,
    loading,
    balanceLoading,
    tiersLoading,
    cardLoading,
    transactionsLoading,
    error,
    purchaseLoading,
    actionLoading,
    fetchBalance,
    loadMoreTransactions,
    handleCreateCheckout,
    handleAddCard,
    handleQuickPurchase,
    handleUpdateCard,
    handleRemoveCard,
    handleConfigureAutoReload,
    refreshData
  } = usePAYGDashboard();

  // Auto-reload settings state
  const [autoReloadEnabled, setAutoReloadEnabled] = useState(false);
  const [autoReloadThreshold, setAutoReloadThreshold] = useState(5);
  const [autoReloadAmount, setAutoReloadAmount] = useState(25);
  const [showAutoReloadSettings, setShowAutoReloadSettings] = useState(false);

  // Custom amount state
  const [showCustomAmount, setShowCustomAmount] = useState(false);
  const [customAmount, setCustomAmount] = useState('');

  // Toast state
  const [toast, setToast] = useState(null);

  // Check URL params for success/cancel/card_updated/card_added
  useEffect(() => {
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');
    const cardUpdated = searchParams.get('card_updated');
    const cardAdded = searchParams.get('card_added');

    if (success === 'true') {
      setToast({ type: 'success', message: 'Credits added successfully!' });
      refreshData();
      setSearchParams({});
    } else if (cancelled === 'true') {
      setToast({ type: 'info', message: 'Purchase cancelled.' });
      setSearchParams({});
    } else if (cardUpdated === 'true') {
      setToast({ type: 'success', message: 'Card updated successfully!' });
      refreshData();
      setSearchParams({});
    } else if (cardAdded === 'true') {
      setToast({ type: 'success', message: 'Card added successfully!' });
      refreshData();
      setSearchParams({});
    }
  }, [searchParams, refreshData, setSearchParams]);

  // Sync auto-reload state with balance data
  useEffect(() => {
    if (balance) {
      setAutoReloadEnabled(balance.auto_reload_enabled || false);
      setAutoReloadThreshold(balance.auto_reload_threshold || 5);
      setAutoReloadAmount(balance.auto_reload_amount || 25);
    }
  }, [balance]);

  // Auto-hide toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleTierPurchase = async (tierAmount) => {
    if (savedCard?.has_saved_card) {
      try {
        const result = await handleQuickPurchase(tierAmount);
        if (result.success) {
          // Use display_credit_added for user-facing message
          const creditsAdded = result.display_credit_added || result.credit_added || tierAmount;
          setToast({ type: 'success', message: result.message || `Added $${creditsAdded?.toFixed(2)} credits!` });
        } else {
          setToast({ type: 'error', message: result.message || 'Purchase failed.' });
        }
      } catch (err) {
        // Handle error response from API
        const errorMessage = err.response?.data?.detail || 'Purchase failed. Please try again.';
        setToast({ type: 'error', message: errorMessage });
      }
    } else {
      await handleCreateCheckout(tierAmount);
    }
  };

  const handleCustomPurchase = async () => {
    const amount = parseFloat(customAmount);
    if (isNaN(amount) || amount < 1 || amount > 500) {
      setToast({ type: 'error', message: 'Please enter an amount between $1 and $500.' });
      return;
    }

    if (savedCard?.has_saved_card) {
      try {
        const result = await handleQuickPurchase(null, amount);
        if (result.success) {
          // Use display_credit_added for user-facing message
          const creditsAdded = result.display_credit_added || result.credit_added || amount;
          setToast({ type: 'success', message: result.message || `Added $${creditsAdded?.toFixed(2)} credits!` });
          setCustomAmount('');
          setShowCustomAmount(false);
        } else {
          setToast({ type: 'error', message: result.message || 'Purchase failed.' });
        }
      } catch (err) {
        const errorMessage = err.response?.data?.detail || 'Purchase failed. Please try again.';
        setToast({ type: 'error', message: errorMessage });
      }
    } else {
      await handleCreateCheckout(amount);
    }
  };

  const handleSaveAutoReload = async () => {
    try {
      await handleConfigureAutoReload(autoReloadEnabled, autoReloadThreshold, autoReloadAmount);
      setToast({
        type: 'success',
        message: autoReloadEnabled
          ? `Auto-reload configured: Add $${autoReloadAmount} when balance falls below $${autoReloadThreshold}`
          : 'Auto-reload disabled'
      });
      setShowAutoReloadSettings(false);
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to configure auto-reload.' });
    }
  };

  const handleRemoveCardClick = async () => {
    if (window.confirm('Are you sure you want to remove your saved card?')) {
      try {
        await handleRemoveCard();
        setToast({ type: 'success', message: 'Card removed successfully.' });
      } catch (err) {
        setToast({ type: 'error', message: 'Failed to remove card.' });
      }
    }
  };

  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const now = Date.now() / 1000;
    const diff = now - timestamp;

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
    return formatDate(timestamp);
  };

  const getCardBrandIcon = (brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return 'VISA';
      case 'mastercard':
        return 'MC';
      case 'amex':
        return 'AMEX';
      default:
        return brand?.toUpperCase() || 'CARD';
    }
  };

  // Show loading spinner only for initial auth check
  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#0a0a0f] text-white">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-[#0a0a0f] p-8">
        <Card className="w-full max-w-lg border-red-500/20 bg-[#121219]/90 backdrop-blur-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-500/10 rounded-full flex items-center justify-center border border-red-500/20">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <CardTitle className="text-2xl text-red-500">Dashboard Error</CardTitle>
            <CardDescription>
              We encountered an issue while loading your dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-red-500/5 border border-red-500/10 rounded-lg p-4 font-mono text-xs text-red-400 break-words">
              {error}
            </div>
            <div className="flex justify-center">
              <Button onClick={() => navigate('/vscode/signin')} variant="outline" className="gap-2">
                Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Use display_balance for user-facing calculations
  const balancePercentage = balance?.total_purchased > 0
    ? ((balance.display_balance || balance.balance) / balance.total_purchased) * 100
    : 0;

  return (
    <div className="flex min-h-screen bg-[#0a0a0f]">
      {/* Sidebar */}
      <div className="w-[280px] h-screen bg-[#121219]/95 backdrop-blur-xl border-r border-white/10 flex flex-col fixed left-0 top-0 z-50">
        <div className="p-6 border-b border-white/10 bg-white/5">
          <div className="flex items-center justify-start">
            <img src={sixthLogo} alt="Sixth Logo" className="h-8" />
          </div>
        </div>

        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white font-bold shadow-lg shadow-primary/20">
              {getUserInitials(userInfo?.fullname)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-medium truncate">{userInfo?.fullname || 'User'}</div>
              <div className="text-xs text-muted-foreground truncate">{userInfo?.email || 'user@example.com'}</div>
              <span className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-green-500/20 text-green-400">
                Pay-As-You-Go
              </span>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-6 px-4 space-y-1 overflow-y-auto">
          <button
            onClick={() => setActiveTab('credits')}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
              activeTab === 'credits'
                ? "bg-primary/10 text-white shadow-sm shadow-primary/5"
                : "text-muted-foreground hover:text-white hover:bg-white/5"
            )}
          >
            <Wallet className={cn("w-4 h-4", activeTab === 'credits' ? "text-primary" : "text-muted-foreground")} />
            Credits Dashboard
          </button>
          <button
            onClick={() => setActiveTab('billing')}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
              activeTab === 'billing'
                ? "bg-primary/10 text-white shadow-sm shadow-primary/5"
                : "text-muted-foreground hover:text-white hover:bg-white/5"
            )}
          >
            <CreditCard className={cn("w-4 h-4", activeTab === 'billing' ? "text-primary" : "text-muted-foreground")} />
            Billing
          </button>
          <button
            onClick={() => setActiveTab('contact')}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
              activeTab === 'contact'
                ? "bg-primary/10 text-white shadow-sm shadow-primary/5"
                : "text-muted-foreground hover:text-white hover:bg-white/5"
            )}
          >
            <MessageSquare className={cn("w-4 h-4", activeTab === 'contact' ? "text-primary" : "text-muted-foreground")} />
            Contact Support
          </button>
        </nav>

        {/* Plan Switcher */}
        <div className="p-4 border-t border-white/10">
          <PlanSwitcher currentPlan="sixth_credit" />
        </div>

        <div className="p-4 border-t border-white/10">
          <button
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 p-3 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 hover:text-red-300 transition-all font-medium text-sm"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pl-[280px]">
        <div className="p-8">
          {/* Toast Notification */}
          {toast && (
            <div className={cn(
              "fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg transition-all",
              toast.type === 'success' && "bg-green-500/90 text-white",
              toast.type === 'error' && "bg-red-500/90 text-white",
              toast.type === 'info' && "bg-blue-500/90 text-white"
            )}>
              {toast.type === 'success' && <CheckCircle className="w-5 h-5" />}
              {toast.type === 'error' && <XCircle className="w-5 h-5" />}
              {toast.type === 'info' && <AlertCircle className="w-5 h-5" />}
              <span className="font-medium">{toast.message}</span>
              <button onClick={() => setToast(null)} className="ml-2 hover:opacity-70">
                <XCircle className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Credits Dashboard Tab */}
          {activeTab === 'credits' && (
            <>
              <div className="mb-8">
                <h1 className="text-2xl font-bold text-white mb-2">Credits Dashboard</h1>
                <p className="text-sm text-muted-foreground">Manage your pay-as-you-go credits and billing</p>
              </div>

              {/* PAYG Retirement Banner */}
              <div className="max-w-5xl mb-6">
                <div className="relative overflow-hidden rounded-lg border border-purple-500/20 bg-gradient-to-r from-purple-500/10 via-violet-500/5 to-transparent p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-purple-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-white mb-1">
                        Fixed Subscription Plans Are Now Available
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        We're transitioning from credits to fixed plans. Once your current credits are exhausted,
                        new credit purchases will no longer be available. Subscribe to a plan to continue using
                        Sixth without interruption.
                      </p>
                    </div>
                    <Button
                      onClick={() => navigate('/pricing')}
                      className="flex-shrink-0 bg-gradient-to-r from-purple-500 to-violet-500 hover:opacity-90 text-white border-0 gap-2 text-sm"
                      size="sm"
                    >
                      View Plans <ArrowUpRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="space-y-6 max-w-5xl">
            {/* Current Balance */}
            {balanceLoading && !balance ? (
              <BalanceSkeleton />
            ) : (
              <Card className="bg-[#121219]/80 border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Wallet className="w-5 h-5 text-primary" />
                      <CardTitle>Current Balance</CardTitle>
                    </div>
                    <button
                      onClick={fetchBalance}
                      disabled={balanceLoading}
                      className="p-2 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50"
                      title="Refresh balance"
                    >
                      <RefreshCw className={cn("w-4 h-4 text-muted-foreground", balanceLoading && "animate-spin")} />
                    </button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="text-4xl font-bold text-white">
                          {balance?.formatted_balance || `$${(balance?.display_balance || balance?.balance || 0).toFixed(2)}`}
                        </span>
                        {balanceLoading && balance && (
                          <Loader2 className="w-5 h-5 text-primary animate-spin" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Available API credits
                      </p>
                    </div>
                    <Button
                      onClick={() => setShowCustomAmount(!showCustomAmount)}
                      className="gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Credits
                    </Button>
                  </div>

                  {/* Balance Progress Bar */}
                  <div className="space-y-2">
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-primary to-purple-500 transition-all duration-500"
                        style={{ width: `${Math.min(balancePercentage, 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{balancePercentage.toFixed(0)}% remaining</span>
                      <span>Total purchased: ${balance?.total_purchased?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>

                  {/* Low Balance Warning */}
                  {balance?.low_balance_warning && (
                    <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-yellow-500" />
                      <span className="text-sm text-yellow-200">
                        Low balance! Consider adding more credits to avoid service interruption.
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Quick Top-Up */}
            {tiersLoading && !tiers ? (
              <TiersSkeleton />
            ) : (
              <Card className="bg-[#121219]/80 border-white/10">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-primary" />
                    <CardTitle>Quick Top-Up</CardTitle>
                  </div>
                  <CardDescription>
                    {savedCard?.has_saved_card
                      ? 'Click to instantly add credits with your saved card'
                      : 'Select an amount to purchase credits'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                    {tiers?.tiers?.map((tier) => (
                      <button
                        key={tier.amount}
                        onClick={() => handleTierPurchase(tier.amount)}
                        disabled={purchaseLoading}
                        className={cn(
                          "p-4 rounded-lg border transition-all duration-200 text-center",
                          "bg-white/5 border-white/10 hover:bg-white/10 hover:border-primary/50",
                          purchaseLoading && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        <div className="text-lg font-bold text-white">${tier.amount}</div>
                      </button>
                    ))}
                  </div>

                  {/* Custom Amount */}
                  {showCustomAmount && (
                    <div className="mt-4 p-4 bg-white/5 rounded-lg border border-white/10">
                      <div className="flex items-center gap-3">
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                          <Input
                            type="number"
                            min="1"
                            max="500"
                            placeholder="Enter amount (1-500)"
                            value={customAmount}
                            onChange={(e) => setCustomAmount(e.target.value)}
                            className="pl-7 bg-white/5 border-white/10"
                          />
                        </div>
                        <Button
                          onClick={handleCustomPurchase}
                          disabled={purchaseLoading || !customAmount}
                        >
                          {purchaseLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Purchase'
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Payment Method */}
            {cardLoading && !savedCard ? (
              <CardSkeleton />
            ) : (
              <Card className="bg-[#121219]/80 border-white/10">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-primary" />
                    <CardTitle>Payment Method</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {savedCard?.has_saved_card ? (
                    <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/5">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-blue-400 rounded flex items-center justify-center text-white text-xs font-bold">
                          {getCardBrandIcon(savedCard.card_brand)}
                        </div>
                        <div>
                          <div className="text-white font-medium">
                            {savedCard.card_brand?.charAt(0).toUpperCase() + savedCard.card_brand?.slice(1)} ending in {savedCard.card_last4}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Expires {savedCard.card_exp_month}/{savedCard.card_exp_year}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleUpdateCard}
                          disabled={actionLoading}
                        >
                          Update
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleRemoveCardClick}
                          disabled={actionLoading}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CreditCard className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground mb-4">No payment method saved</p>
                      <Button onClick={handleAddCard} disabled={actionLoading}>
                        Add Payment Card
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Auto-Reload */}
            <Card className="bg-[#121219]/80 border-white/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 text-primary" />
                    <CardTitle>Auto-Reload</CardTitle>
                  </div>
                  <Switch
                    checked={autoReloadEnabled}
                    onCheckedChange={(checked) => {
                      setAutoReloadEnabled(checked);
                      setShowAutoReloadSettings(true);
                    }}
                  />
                </div>
                <CardDescription>
                  Automatically add credits when your balance falls below a threshold
                </CardDescription>
              </CardHeader>
              {(showAutoReloadSettings || autoReloadEnabled) && (
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-muted-foreground mb-2 block">
                          Reload when balance falls below
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                          <Input
                            type="number"
                            min="1"
                            max="100"
                            value={autoReloadThreshold}
                            onChange={(e) => setAutoReloadThreshold(parseFloat(e.target.value) || 5)}
                            className="pl-7 bg-white/5 border-white/10"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground mb-2 block">
                          Amount to reload
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                          <Input
                            type="number"
                            min="5"
                            max="500"
                            value={autoReloadAmount}
                            onChange={(e) => setAutoReloadAmount(parseFloat(e.target.value) || 25)}
                            className="pl-7 bg-white/5 border-white/10"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        onClick={handleSaveAutoReload}
                        disabled={actionLoading}
                      >
                        {actionLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : null}
                        Save Settings
                      </Button>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Recent Transactions */}
            {transactionsLoading && !transactions ? (
              <TransactionsSkeleton />
            ) : (
              <Card className="bg-[#121219]/80 border-white/10">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" />
                    <CardTitle>Recent Transactions</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {transactions?.transactions?.length > 0 ? (
                    <div className="space-y-2">
                      {transactions.transactions.map((txn) => (
                        <div
                          key={txn.id}
                          className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/5 hover:bg-white/10 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              "w-8 h-8 rounded-full flex items-center justify-center",
                              txn.type === 'purchase' || txn.type === 'auto_reload'
                                ? "bg-green-500/10"
                                : txn.type === 'refund'
                                  ? "bg-blue-500/10"
                                  : "bg-red-500/10"
                            )}>
                              {txn.type === 'purchase' || txn.type === 'auto_reload' ? (
                                <ArrowUpRight className="w-4 h-4 text-green-500" />
                              ) : txn.type === 'refund' ? (
                                <TrendingUp className="w-4 h-4 text-blue-500" />
                              ) : (
                                <ArrowDownRight className="w-4 h-4 text-red-500" />
                              )}
                            </div>
                            <div>
                              <div className="text-white font-medium text-sm">
                                {txn.description}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {formatRelativeTime(txn.created_at)}
                              </div>
                            </div>
                          </div>
                          <div className={cn(
                            "font-semibold",
                            txn.amount >= 0 ? "text-green-500" : "text-red-400"
                          )}>
                            {txn.amount >= 0 ? '+' : ''}{txn.amount < 0 ? txn.amount.toFixed(4) : `$${txn.amount.toFixed(2)}`}
                          </div>
                        </div>
                      ))}

                      {transactionsHasMore && (
                        <div className="text-center pt-4">
                          <Button
                            variant="ghost"
                            onClick={loadMoreTransactions}
                            className="text-primary hover:text-primary/80"
                          >
                            Load More
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No transactions yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Usage Summary */}
            <Card className="bg-[#121219]/80 border-white/10">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  <CardTitle>Usage Summary</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-white/5 rounded-lg border border-white/5">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Total Purchased</div>
                    {balanceLoading && !balance ? (
                      <Skeleton className="h-8 w-24" />
                    ) : (
                      <div className="text-2xl font-bold text-white">
                        ${balance?.total_purchased?.toFixed(2) || '0.00'}
                      </div>
                    )}
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg border border-white/5">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Total Consumed</div>
                    {balanceLoading && !balance ? (
                      <Skeleton className="h-8 w-24" />
                    ) : (
                      <div className="text-2xl font-bold text-white">
                        ${balance?.total_consumed?.toFixed(2) || '0.00'}
                      </div>
                    )}
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg border border-white/5">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Current Balance</div>
                    {balanceLoading && !balance ? (
                      <Skeleton className="h-8 w-24" />
                    ) : (
                      <div className="text-2xl font-bold text-primary">
                        {balance?.formatted_balance || `$${(balance?.display_balance || balance?.balance || 0).toFixed(2)}`}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
            </>
          )}

          {/* Billing Tab */}
          {activeTab === 'billing' && (
            <>
              <div className="mb-8">
                <h1 className="text-2xl font-bold text-white mb-2">Billing</h1>
                <p className="text-sm text-muted-foreground">View your purchase history and invoices</p>
              </div>

              <div className="space-y-6 max-w-5xl">
                {/* Account Summary */}
                <Card className="bg-[#121219]/80 border-white/10">
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Wallet className="w-5 h-5 text-primary" />
                      <CardTitle>Account Summary</CardTitle>
                    </div>
                    <CardDescription>Your Pay-As-You-Go account overview</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-white/5 rounded-lg border border-white/5">
                        <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-2">
                          <Wallet className="w-4 h-4" />
                          Current Balance
                        </div>
                        <div className="text-2xl font-bold text-primary">
                          {balance?.formatted_balance || `$${(balance?.display_balance || balance?.balance || 0).toFixed(2)}`}
                        </div>
                      </div>
                      <div className="p-4 bg-white/5 rounded-lg border border-white/5">
                        <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-2">
                          <TrendingUp className="w-4 h-4" />
                          Total Purchased
                        </div>
                        <div className="text-2xl font-bold text-white">
                          ${balance?.total_purchased?.toFixed(2) || '0.00'}
                        </div>
                      </div>
                      <div className="p-4 bg-white/5 rounded-lg border border-white/5">
                        <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-2">
                          <Clock className="w-4 h-4" />
                          Total Used
                        </div>
                        <div className="text-2xl font-bold text-white">
                          ${balance?.total_consumed?.toFixed(2) || '0.00'}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Payment Method */}
                <Card className="bg-[#121219]/80 border-white/10">
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-primary" />
                      <CardTitle>Payment Method</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {savedCard?.has_saved_card ? (
                      <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/5">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-blue-400 rounded flex items-center justify-center text-white text-xs font-bold">
                            {getCardBrandIcon(savedCard.card_brand)}
                          </div>
                          <div>
                            <div className="text-white font-medium">
                              {savedCard.card_brand?.charAt(0).toUpperCase() + savedCard.card_brand?.slice(1)} ending in {savedCard.card_last4}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Expires {savedCard.card_exp_month}/{savedCard.card_exp_year}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleUpdateCard}
                            disabled={actionLoading}
                          >
                            Update
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleRemoveCardClick}
                            disabled={actionLoading}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <CreditCard className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground mb-4">No payment method saved</p>
                        <Button onClick={handleAddCard} disabled={actionLoading}>
                          Add Payment Card
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Purchase History */}
                <Card className="bg-[#121219]/80 border-white/10">
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-primary" />
                      <CardTitle>Purchase History</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {transactions?.transactions?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                          <thead className="text-xs text-muted-foreground uppercase bg-white/5">
                            <tr>
                              <th className="px-4 py-3 rounded-l-lg">Date</th>
                              <th className="px-4 py-3">Description</th>
                              <th className="px-4 py-3 rounded-r-lg text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {transactions.transactions.map((txn) => (
                              <tr key={txn.id} className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                                <td className="px-4 py-3 text-white">{formatDate(txn.created_at)}</td>
                                <td className="px-4 py-3 text-muted-foreground">{txn.description}</td>
                                <td className={cn(
                                  "px-4 py-3 text-right font-semibold",
                                  txn.amount >= 0 ? "text-green-500" : "text-red-400"
                                )}>
                                  {txn.amount >= 0 ? '+' : ''}{txn.amount < 0 ? txn.amount.toFixed(4) : `$${txn.amount.toFixed(2)}`}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No purchase history found.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}

          {/* Contact Support Tab */}
          {activeTab === 'contact' && (
            <PAYGContactSupport />
          )}
        </div>
      </main>
    </div>
  );
};

// Contact Support Component for PAYG
const PAYGContactSupport = () => {
  const [copied, setCopied] = useState(false);
  const supportEmail = 'ope@trysixth.com';

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(supportEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = supportEmail;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">Contact Support</h1>
        <p className="text-sm text-muted-foreground">Get help with your account or technical issues</p>
      </div>

      <div className="max-w-2xl">
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              <CardTitle>Email Support</CardTitle>
            </div>
            <CardDescription>
              Send us an email and we'll respond within 24 hours during business days.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-black/40 border border-white/10 rounded-lg p-4 flex items-center justify-between">
              <code className="text-sm text-white font-mono">{supportEmail}</code>
              <Button
                variant="ghost"
                size="sm"
                onClick={copyToClipboard}
                className={cn(
                  "h-8 gap-2 transition-colors",
                  copied ? "text-green-500 hover:text-green-600 hover:bg-green-500/10" : "text-primary hover:text-primary/80 hover:bg-primary/10"
                )}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" /> Copy
                  </>
                )}
              </Button>
            </div>

            <Button
              className="w-full gap-2"
              onClick={() => window.open(`mailto:${supportEmail}`)}
            >
              <ExternalLink className="w-4 h-4" /> Open Mail App
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default PAYGDashboard;
